import { getDocument, type PDFDocumentProxy } from "pdfjs-dist";
import { canv } from "@/canv/global";
import { getNumberProperty, getStringProperty, isBrowser } from "@/canv/utils";
import { ElementBase } from "@/elementBase";
import { PDF_COVER_STRING, PPT_COVER_STRING } from "@/elements/documentViewer/consts";
import { formatFileSize } from "@/utils";
import { widgetOption } from "@/widgetMenu/widgetMenu";

export type ViewMode = "compact" | "expanded";
type FileExtension = "pdf" | "ppt" | "pptx";

const prepareDocumentUrl = (src: string) => {
    const url = new URL(src);
    const token = canv.getInitDataProp("boardAccessToken");
    if (token) {
        url.searchParams.set("boardAccessToken", token);
    }
    const [, path] = url.href.split("/api/");
    if (path) {
        return `${canv.host}/api/${path}`;
    }
    return src;
};

const BASE_COMPACT_WIDTH = 80;
const BASE_COMPACT_HEIGHT = 103;

export class DocumentViewerWidget extends ElementBase {
    static coverCache: Record<FileExtension, HTMLCanvasElement | null> = {
        pdf: null,
        ppt: null,
        pptx: null,
    };

    constructor(documentName?: string, width = 80, height = 103, file?: Blob) {
        super();
        this.width = width;
        this.height = height;
        if (documentName) {
            this.documentName = documentName;
        }
        if (file) {
            this.isFirstUpload = true;
            this.getOrInitPages(file);
        }
        this.initCompactImage();
        this.updateDims();
    }

    async initCompactImage() {
        this.compactImg = await DocumentViewerWidget.getCachedCoverCanvas(this.fileExtension);
        canv.redraw();
    }

    static async getCachedCoverCanvas(ext: FileExtension): Promise<HTMLCanvasElement> {
        const existing = DocumentViewerWidget.coverCache[ext];
        if (existing) return existing;

        const svg = ext === "pdf" ? PDF_COVER_STRING : PPT_COVER_STRING;
        const svgUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);

        const img = new Image();
        img.src = svgUrl;
        await img.decode(); // ждем полной загрузки

        const canvas = document.createElement("canvas");
        canvas.width = BASE_COMPACT_WIDTH * 10;
        canvas.height = BASE_COMPACT_HEIGHT * 10;
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        DocumentViewerWidget.coverCache[ext] = canvas; // сохраняем канвас в кэш
        canv.redraw();
        return canvas;
    }

    translate(x: number, y: number) {
        this.startX += x;
        this.startY += y;
        this.updateDims();
    }

    resize(x: number, y: number, catchPointer: string) {
        if (["sideRight", "sideBottom", "sideLeft", "sideTop"].includes(catchPointer)) return;

        const diffValue = (x + y) / 2;
        // Насколько надо сильнее менять высоту по сравнению с шириной, чтобы сохранить пропорции
        const hvRatio = this.height / this.width;

        this.height += hvRatio * diffValue;
        this.width += diffValue;
    }

    updateDims() {
        if (!isBrowser()) return;
        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    async setPdf(blob: Blob) {
        const arrayBuffer = await blob.arrayBuffer();
        const pdf: PDFDocumentProxy = await getDocument({ data: arrayBuffer }).promise;

        this.totalPages = pdf.numPages;
        this.pages = [];

        const scale = 2;
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale });

            const canvas = document.createElement("canvas");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext("2d");

            if (this.isFirstUpload && i === this.currentPageNumber) {
                canv.eventManager.openRecord();
                this.fixState("full");
                this.width = viewport.width;
                this.height = viewport.height;
                this.updateDims();
                this.saveState("full");
                canv.eventManager.closeRecord();
            }

            if (ctx) {
                await page.render({
                    canvasContext: ctx,
                    viewport,
                    canvas,
                }).promise;
            }

            this.pages.push(canvas);
        }

        canv.redraw();
    }

    getWidgetOptions() {
        if (this.loading) return [];

        const widgetOptions: widgetOption[] = [];
        const isEditorOrOwner = canv.isBoardModeEditOrOwner();
        widgetOptions.push(new widgetOption("documentName", this.documentName || "", this.UID));
        if (this.pages.length) {
            widgetOptions.push(new widgetOption("documentMode", this.viewMode, this.UID));
        }
        if (this.viewMode !== "compact") {
            if (this.pages.length > 1) {
                widgetOptions.push(
                    new widgetOption(
                        "documentTurnPage",
                        { currentPageNumber: this.currentPageNumber, totalPages: this.totalPages },
                        this.UID,
                    ),
                );
            }
            if (isEditorOrOwner) {
                widgetOptions.push(
                    new widgetOption(
                        "documentPreviewPage",
                        {
                            previewPageNumber: this.previewPageNumber,
                            currentPageNumber: this.currentPageNumber,
                        },
                        this.UID,
                    ),
                );
            }
        }
        if (isEditorOrOwner && this.src) {
            widgetOptions.push(new widgetOption("documentDownload", null, this.UID));
        }

        return widgetOptions;
    }

    reNameDocument = (name: string) => {
        if (!name) return;

        canv.eventManager.openRecord();
        this.fixState("full");
        this.documentName = `${name}.${this.fileExtension}`;
        this.saveState("full");
        canv.eventManager.closeRecord();

        canv.widgetMenu.reopen();
        canv.redraw();
    };

    goToPage = (pageNumber: number) => {
        if (pageNumber < 1 || pageNumber > this.totalPages) return;
        this.currentPageNumber = pageNumber;
        canv.widgetMenu.reopen();
        canv.redraw();
    };
    switchViewMode = () => {
        canv.eventManager.openRecord();
        this.fixState("full");

        this.viewMode = this.viewMode === "compact" ? "expanded" : "compact";

        let k = 1;
        if (this.viewMode === "expanded") {
            k = this.height / BASE_COMPACT_HEIGHT;
        } else {
            const pages = this.getOrInitPages();
            const pageCanvas = pages[this.currentPageNumber - 1];
            if (pageCanvas) {
                k = this.height / pageCanvas.height;
            }
        }

        if (this.viewMode === "expanded") {
            const pages = this.getOrInitPages();
            const pageCanvas = pages[this.currentPageNumber - 1];

            const maxViewportWidth = canv.canvas.width / 0.018;
            const maxViewportHeight = canv.canvas.height / 0.018;

            const expandWidth = pageCanvas.width * k;
            const expandHeight = pageCanvas.height * k;

            if (pageCanvas) {
                if (
                    expandWidth > maxViewportWidth * 0.9 ||
                    expandHeight > maxViewportHeight * 0.9
                ) {
                    const pageRatio = pageCanvas.width / pageCanvas.height;
                    if (pageRatio >= 1) {
                        this.width = maxViewportWidth * 0.9;
                        this.height = this.width / pageRatio;
                    } else {
                        this.height = maxViewportHeight * 0.9;
                        this.width = this.height * pageRatio;
                    }
                } else {
                    this.width = pageCanvas.width * k;
                    this.height = pageCanvas.height * k;
                }
            }
        } else {
            this.width = BASE_COMPACT_WIDTH * k;
            this.height = BASE_COMPACT_HEIGHT * k;
        }
        this.updateDims();

        this.saveState("full");
        canv.eventManager.closeRecord();

        canv.widgetMenu.reopen();
        canv.redraw();
    };

    setPreviewPage = (pageNumber: number) => {
        canv.eventManager.openRecord();
        this.fixState("full");
        this.previewPageNumber = pageNumber;
        this.saveState("full");
        canv.eventManager.closeRecord();
        canv.widgetMenu.reopen();
        canv.redraw();
    };

    downloadDocument = async () => {
        if (!this.src) return;

        try {
            const url = prepareDocumentUrl(this.src);
            const resp = await fetch(url);
            if (!resp.ok) {
                throw new Error(`Ошибка загрузки документа: ${resp.statusText}`);
            }

            const blob = await resp.blob();
            const objectUrl = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = this.documentName
                ? `${this.documentName}`
                : `document.${this.fileExtension}`;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error("Не удалось скачать документ", err);
        }
    };

    initImage(src: string, forcedProps = undefined, keepBase64 = false) {
        this.src = src;
    }

    setLoading(loading: boolean) {
        this.loading = loading;
    }

    getOrInitPages(blob?: Blob) {
        if (this.pages.length > 0) return this.pages;
        if (this.src && !this.triedToLoad) {
            this.triedToLoad = true;
            this.loading = true;

            if (blob) {
                this.setPdf(blob).finally(() => {
                    this.loading = false;
                    canv.redraw();
                });
            } else {
                const url = prepareDocumentUrl(this.src);
                fetch(url)
                    .then((resp) => {
                        if (!resp.ok) {
                            throw new Error(`Ошибка загрузки документа: ${resp.statusText}`);
                        }
                        return resp.blob();
                    })
                    .then((blob) => {
                        this.fileSize = blob.size;
                        this.setPdf(blob);
                    })
                    .catch((err) => {
                        console.error("Не удалось загрузить документ", err);
                    })
                    .finally(() => {
                        this.loading = false;
                        canv.redraw();
                    });
            }
        }

        return [];
    }

    drawRect() {
        canv.ctx.save();
        canv.ctx.fillStyle = "#fff";
        canv.ctx.fillRect(this.startX, this.startY, this.width, this.height);
        canv.ctx.restore();
    }

    drawCompactMode() {
        const canvas = this.compactImg;
        if (canvas) {
            canv.ctx.drawImage(canvas, this.startX, this.startY, this.width, this.height);
        } else {
            this.drawRect();
        }
    }

    drawExpandedMode() {
        const pages = this.getOrInitPages();
        const pageCanvas = pages[this.currentPageNumber - 1];
        if (!pageCanvas) {
            this.drawRect();
            return;
        }

        canv.ctx.drawImage(pageCanvas, this.startX, this.startY, this.width, this.height);
    }

    truncateText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
        let width = ctx.measureText(text).width;
        if (width <= maxWidth) return text;

        const ellipsis = "...";
        const ellipsisWidth = ctx.measureText(ellipsis).width;

        let truncated = text;
        while (truncated.length > 0 && width + ellipsisWidth > maxWidth) {
            truncated = truncated.slice(0, -1);
            width = ctx.measureText(truncated).width;
        }
        return truncated + ellipsis;
    }

    drawFileNameAndSize() {
        if (!this.documentName) return;

        const ctx = canv.ctx;
        ctx.save();
        ctx.fillStyle = "#000";
        ctx.textAlign = "center";

        const koef = this.width / BASE_COMPACT_WIDTH;
        ctx.font = `${20 * koef}px Arial`;

        const centerX = this.startX + this.width / 2;
        let y = this.startY + this.height + 20 * koef;

        const name = this.documentName;

        let firstLine = "";
        let secondLine = "";

        if (name.length <= 9) {
            firstLine = name;
        } else if (name.length <= 18) {
            firstLine = name.slice(0, 9);
            secondLine = name.slice(9);
        } else {
            firstLine = name.slice(0, 9);
            const rest = name.slice(9);
            secondLine = this.truncateText(ctx, rest, this.width); // ограничиваем шириной
        }

        ctx.fillText(firstLine, centerX, y);

        if (secondLine) {
            y += 22 * koef;
            ctx.fillText(secondLine, centerX, y);
        }

        // размер файла
        if (this.fileSize !== null) {
            ctx.fillStyle = "#7C7C91";
            y += 25 * koef;
            ctx.font = `${16 * koef}px Arial`;
            const sizeStr = formatFileSize(this.fileSize);
            ctx.fillText(sizeStr, centerX, y);
        }

        ctx.restore();
    }

    draw() {
        if (!this.triedToLoad) {
            this.getOrInitPages();
        }
        try {
            if (this.loading) {
                this.drawRect();
                return;
            }
            if (this.viewMode === "compact") {
                this.drawCompactMode();
                this.drawFileNameAndSize();
            } else {
                this.drawExpandedMode();
            }
        } catch (err) {
            console.error(err);
        }
    }

    getFixType(reason: string) {
        let fixType = "full";

        switch (reason) {
            case "move":
                fixType = "move";
                break;
            case "resize":
                fixType = "resize";
                break;
        }

        return fixType;
    }

    getState(fixType: string) {
        switch (fixType) {
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                };
            case "resize":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                };
            case "full":
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "documentWidget",
                    src: this.src,
                    documentName: this.documentName,
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    previewPageNumber: this.previewPageNumber,
                    fileExtension: this.fileExtension,
                    viewMode: this.viewMode,
                };
            default:
                return {};
        }
    }

    loadState(stateObj: this, fixType: string) {
        switch (fixType) {
            case "move":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                break;
            case "resize":
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                break;
            case "full":
                this.UID = getStringProperty("UID", stateObj);
                this.parentUID = getStringProperty("parentUID", stateObj);
                this.startX = getNumberProperty("startX", stateObj);
                this.startY = getNumberProperty("startY", stateObj);
                this.width = getNumberProperty("width", stateObj);
                this.height = getNumberProperty("height", stateObj);
                this.src = stateObj.src;
                this.documentName = getStringProperty("documentName", stateObj);
                this.previewPageNumber = getNumberProperty("previewPageNumber", stateObj);
                this.currentPageNumber = getNumberProperty("previewPageNumber", stateObj);
                this.fileExtension = getStringProperty("fileExtension", stateObj) as FileExtension;
                this.viewMode = getStringProperty("viewMode", stateObj) as ViewMode;
                break;
        }

        this.updateDims();
    }

    constructorName: "documentViewer" = "documentViewer";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
    src: string | null = null;
    documentName: string | null = null;
    viewMode: ViewMode = "expanded";
    compactImg: HTMLCanvasElement | null = null;
    fileExtension: FileExtension = "pdf";
    pages: HTMLCanvasElement[] = [];
    currentPageNumber = 1;
    previewPageNumber = 1;
    totalPages = 1;
    loading = false;
    triedToLoad = false;
    fileSize: number | null = null;
    isFirstUpload = false;
}
